import React, { useRef } from 'react';
import {
  FormControl,
  Typography,
  OutlinedInput as MuiOutlinedInput,
  FormHelperText,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FastField, useField } from 'formik';
import { useFocusOnError } from '../../shared/hooks';

const getStyles = (theme) => {
  const root = {
    borderRadius: 0,
    backgroundColor: 'white',
  };

  return {
    title: {
      userSelect: 'none',
      fontWeight: 700,
      fontFamily: 'Source Sans Pro',
      color: theme.palette.text.main,
      fontSize: 16,
      [theme.breakpoints.up('xs')]: { fontSize: 16 },
      [theme.breakpoints.up('sm')]: { fontSize: 16 },
      [theme.breakpoints.up('md')]: { fontSize: 18 },
      [theme.breakpoints.up('lg')]: { fontSize: 20 },
      [theme.breakpoints.up('xl')]: { fontSize: 20 },
    },
    titleColor: {
      color: '#908e88',
      fontWeight: 100,
    },
    titleMargin: {
      marginLeft: '5px',
    },
    notchedOutline: {
      border: '3px solid #f0f0f0',
    },
    input: {
      paddingTop: 0,
    },
    inputLightRoot: {
      ...root,
      border: '1px solid rgba(204, 204, 204, 1)',
    },
    inputDarkRoot: {
      ...root,
      border: '1px solid rgba(204, 204, 204, 1)',
    },
    inputOutlined: {
      padding: '13px 14px',
      fontFamily: 'Source Sans Pro',
    },
  };
};

const InputText = ({
  name,
  label,
  placeholder,
  inputProps,
  maxLength,
  className,
  lightBorder = true,
  startIcon,
  endIcon,
  labelGrey = false,
  labelMargin = false,
  boxShadow,
  uppercase,
  shrink,
  inputComponent,
  ...props
}) => {
  const fieldRef = useRef();
  const theme = useTheme();
  const classes = getStyles(theme);
  const [, metaError] = useField(name);
  useFocusOnError({ fieldName: name, fieldId: `form-field-${name}` });

  return (
    <div className={className}>
      <FormControl fullWidth variant="outlined" sx={classes.input}>
        {label && (
          <Typography
            component="div"
            sx={{
              ...classes.title,
              ...(labelGrey ? classes.titleColor : {}),
              ...(labelMargin ? classes.titleMargin : {}),
            }}
          >
            {label}
          </Typography>
        )}
        <FastField name={name}>
          {({ field, meta }) => {
            return (
              <MuiOutlinedInput
                {...field}
                {...props}
                type="text"
                error={meta.touched && !!meta.error}
                id={`form-field-${name}`}
                placeholder={placeholder}
                inputProps={{
                  ...inputProps,
                  maxLength: maxLength || 75,
                  autoComplete: 'off',
                  form: {
                    autoComplete: 'off',
                  },
                }}
                {...(inputComponent ? { inputComponent } : {})}
                ref={fieldRef}
                sx={{
                  ...(lightBorder
                    ? classes.inputLightRoot
                    : classes.inputDarkRoot),
                  '& .MuiOutlinedInput-input': classes.inputOutlined,
                  '& .MuiOutlinedInput-notchedOutline': classes.notchedOutline,
                  '&.Mui-error': {
                    border: '1px solid #f44336',
                  },
                }}
                startAdornment={startIcon}
                endAdornment={endIcon}
              />
            );
          }}
        </FastField>
        {metaError.touched && metaError?.error && (
          <FormHelperText error>{metaError.error}</FormHelperText>
        )}
      </FormControl>
    </div>
  );
};

export default InputText;
